import TreeView from '@mui/lab/TreeView';
import TreeItem, { TreeItemProps, treeItemClasses } from '@mui/lab/TreeItem';
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';
import Collapse from '@mui/material/Collapse';
import { useSpring, animated } from '@react-spring/web';
import { TransitionProps } from '@mui/material/transitions';
import { alpha, styled } from '@mui/material/styles';
import ArticleIcon from '@mui/icons-material/Article';
import { Grid } from '@mui/material';
import { FormControl } from '@mui/material';
import { LoadingSpinner, useLoadingSpinner } from '../../Components/LoadingSpinner';
import { selectSelectedWiki, selectWikis, getWikis, setSelectedWiki, selectIsLoaded } from '../../Services/Store/Slices/WikiSlice';
import { useMsal } from '@azure/msal-react';
import { useAppDispatch, useAppSelector } from '../../Services/Store';
import { InputLabel } from '@mui/material';
import { Select } from '@mui/material';
import { SelectChangeEvent } from '@mui/material';
import { MenuItem } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { WikiPage } from '../../Services/Api';
import { useNavigate } from 'react-router-dom';

function MinusSquare(props: SvgIconProps) {
  return (
    <SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
      {/* tslint:disable-next-line: max-line-length */}
      <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z" />
    </SvgIcon>
  );
}
  
function PlusSquare(props: SvgIconProps) {
  return (
    <SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
      {/* tslint:disable-next-line: max-line-length */}
      <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
    </SvgIcon>
  );
}

function TransitionComponent(props: TransitionProps) {
  const style = useSpring({
    from: {
      opacity: 0,
      transform: 'translate3d(20px,0,0)',
    },
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
    },
  });

  return (
    <animated.div style={style}>
      <Collapse {...props} />
    </animated.div>
  );
}

const StyledTreeItem = styled((props: TreeItemProps) => (
  <TreeItem {...props} TransitionComponent={TransitionComponent} />
  ))(({ theme }) => ({
    [`& .${treeItemClasses.iconContainer}`]: {
      '& .close': {
        opacity: 0.3,
      },
    },
    [`& .${treeItemClasses.group}`]: {
      marginLeft: 15,
      paddingLeft: 18,
      borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
    },
}));

function CreateStyledTreeItem(item: WikiPage, navigate: (url: string) => void) {
  if (item.children === null || item.children.length === 0) {
    return(
      <StyledTreeItem key={item.id} nodeId={ item.id } label={ item.displayName } onClick={ () => navigate(`/${item.fileUrl}`) } />
    )
  }
  
  return(
    <StyledTreeItem key={item.id} nodeId={ item.id } label={ item.displayName } onClick={ () => navigate(`/${item.fileUrl}`) }>
      { (item.children ?? []).map((item): JSX.Element => (
        CreateStyledTreeItem(item, navigate)
      )) }
    </StyledTreeItem>
  )
}

export const WikisTreeView = () => {
  const { isLoading, startLoading, stopLoading } = useLoadingSpinner();
  const msalContextRef = useRef(useMsal());
  const dispatch = useAppDispatch();
  const isLoaded = useAppSelector(selectIsLoaded);
  const wikis = useAppSelector(selectWikis);
  const selectedWiki = useAppSelector(selectSelectedWiki);
  const navigate = useNavigate();
  const handleChange = useCallback((event: SelectChangeEvent<string>, _: React.ReactNode): void => {
    const value = event.target.value as string;
    const wiki = wikis.find(e => e.name === value);

    dispatch(setSelectedWiki(wiki));
  }, [wikis, dispatch]);

  useEffect(() => {
    if (!isLoaded) {
      startLoading();
      dispatch(getWikis(msalContextRef.current))
        .finally(() => stopLoading());
    }
  }, [isLoaded, dispatch, startLoading, stopLoading]);

  return (
    <Grid container direction="column" position='relative' sx={{ marginTop: '20px' }} overflow='auto'>
      <FormControl fullWidth>
        <InputLabel id='wiki-selector'>
          Wiki
        </InputLabel>
        <Select
          labelId='wiki-selector'
          value = { selectedWiki?.name ?? '' }
          label='Wiki'
          onChange={ handleChange }
        >
          { wikis.map((item): JSX.Element => (
            <MenuItem key={ item.name } value={ item.name }>
              { item.name }
            </MenuItem>
          )) }
        </Select>
      </FormControl>
      <TreeView
        aria-label='customized'
        defaultExpanded={ ['1'] }
        defaultCollapseIcon={ <MinusSquare /> }
        defaultExpandIcon={ <PlusSquare /> }
        defaultEndIcon={ <ArticleIcon /> }
        sx={{ marginTop: '20px' }}
      >
        { (selectedWiki?.children ?? []).map((item): JSX.Element => (
          CreateStyledTreeItem(item, url => navigate(url))
        )) }
      </TreeView>
      {(isLoading && <LoadingSpinner />)}
    </Grid>
  )
}